import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Plus, 
  Search, 
  FileText,
  Download,
  Eye,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 50;

export default function InvoiceHistory() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [trailerFilter, setTrailerFilter] = useState("");
  const [vinFilter, setVinFilter] = useState("");
  const [licenseFilter, setLicenseFilter] = useState("");
  const [partsFilter, setPartsFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [fetchingMore, setFetchingMore] = useState(false);

  // Debounce search to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch invoices with pagination and retry logic for cold starts
  const fetchInvoices = useCallback(async (page = 1, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds
    
    try {
      if (page === 1 && retryCount === 0) {
        setLoading(true);
      } else if (page !== 1) {
        setFetchingMore(true);
      }
      
      // Build query params
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      
      const [invoicesRes, countRes] = await Promise.all([
        axios.get(`${API}/invoices?${params.toString()}`, { timeout: 30000 }),
        axios.get(`${API}/invoices/count?${params.toString()}`, { timeout: 30000 })
      ]);
      
      setInvoices(invoicesRes.data);
      setTotalCount(countRes.data.count);
      setCurrentPage(page);
    } catch (error) {
      console.error(`Error fetching invoices (attempt ${retryCount + 1}):`, error);
      
      // Retry on network errors or 5xx errors (cold start scenarios)
      if (retryCount < MAX_RETRIES && (
        error.code === 'ECONNABORTED' || 
        error.code === 'ERR_NETWORK' ||
        (error.response && error.response.status >= 500)
      )) {
        console.log(`Retrying in ${RETRY_DELAY}ms...`);
        setTimeout(() => {
          fetchInvoices(page, retryCount + 1);
        }, RETRY_DELAY * (retryCount + 1)); // Exponential backoff
        return;
      }
      
      toast.error("Failed to load invoices. Please refresh the page.");
    } finally {
      if (retryCount === 0 || retryCount >= MAX_RETRIES) {
        setLoading(false);
        setFetchingMore(false);
      }
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchInvoices(1);
  }, [fetchInvoices]);

  // Client-side filtering for vehicle-specific filters (applied after fetching)
  const filteredInvoices = invoices.filter(invoice => {
    // Filter by Truck #
    const truckNumber = invoice.vehicle_info?.truck_number || "";
    const matchesTruck = !truckFilter || 
      truckNumber.toLowerCase().includes(truckFilter.toLowerCase());
    
    // Filter by Trailer #
    const trailerNumber = invoice.vehicle_info?.trailer_number || "";
    const matchesTrailer = !trailerFilter || 
      trailerNumber.toLowerCase().includes(trailerFilter.toLowerCase());
    
    // Filter by VIN
    const vinNumber = invoice.vehicle_info?.serial_vin || "";
    const matchesVin = !vinFilter || 
      vinNumber.toLowerCase().includes(vinFilter.toLowerCase());
    
    // Filter by License #
    const licenseNumber = invoice.vehicle_info?.license_number || "";
    const matchesLicense = !licenseFilter || 
      licenseNumber.toLowerCase().includes(licenseFilter.toLowerCase());
    
    // Filter by Parts Description
    const partsDescriptions = (invoice.items || [])
      .map(item => item.parts_description || "")
      .join(" ")
      .toLowerCase();
    const matchesParts = !partsFilter || 
      partsDescriptions.includes(partsFilter.toLowerCase());
    
    return matchesTruck && matchesTrailer && matchesVin && matchesLicense && matchesParts;
  });

  // Pagination helpers
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const handleDelete = async () => {
    if (!selectedInvoice) return;

    try {
      await axios.delete(`${API}/invoices/${selectedInvoice.id}`);
      toast.success("Invoice deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices(currentPage);
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const calculateTotal = (invoice) => {
    const items = invoice.items || [];
    const totalParts = items.reduce((sum, item) => 
      sum + ((item.parts_qty || 0) * (item.parts_price || 0)), 0);
    const totalLabor = items.reduce((sum, item) => 
      sum + ((item.labor_qty || 0) * (item.labor_price || 0)), 0);
    const subtotal = totalParts + totalLabor + (invoice.road_service_fee || 0) + (invoice.storage_fee || 0);
    // Tax only applies to parts (V2: Labor is tax-exempt)
    const tax = totalParts * ((invoice.tax_rate || 0) / 100);
    return subtotal + tax;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12" data-testid="invoice-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 font-['Manrope'] tracking-tight">
            Invoices
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1 sm:mt-2">
            View and manage all invoices
          </p>
        </div>
        <Link to="/invoices/new">
          <Button 
            className="bg-blue-600 hover:bg-blue-700 gap-2 btn-animate text-sm sm:text-base w-full sm:w-auto"
            data-testid="create-invoice-btn"
          >
            <Plus className="h-4 w-4" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
        {/* Row 1: Search and Status */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by invoice # or customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-sm sm:text-base"
              data-testid="invoice-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 text-sm sm:text-base" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="estimate">Estimate</SelectItem>
                <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Row 2: Truck #, Trailer #, VIN, License Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <Input
              placeholder="Filter by Truck #..."
              value={truckFilter}
              onChange={(e) => setTruckFilter(e.target.value)}
              className="text-sm sm:text-base"
              data-testid="truck-filter-input"
            />
          </div>
          <div>
            <Input
              placeholder="Filter by Trailer #..."
              value={trailerFilter}
              onChange={(e) => setTrailerFilter(e.target.value)}
              className="text-sm sm:text-base"
              data-testid="trailer-filter-input"
            />
          </div>
          <div>
            <Input
              placeholder="Filter by VIN #..."
              value={vinFilter}
              onChange={(e) => setVinFilter(e.target.value)}
              className="text-sm sm:text-base"
              data-testid="vin-filter-input"
            />
          </div>
          <div>
            <Input
              placeholder="Filter by License #..."
              value={licenseFilter}
              onChange={(e) => setLicenseFilter(e.target.value)}
              className="text-sm sm:text-base"
              data-testid="license-filter-input"
            />
          </div>
        </div>
        
        {/* Row 3: Parts Search */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1 sm:max-w-md">
            <Input
              placeholder="Search parts used (e.g., brake pads, oil filter)..."
              value={partsFilter}
              onChange={(e) => setPartsFilter(e.target.value)}
              className="text-sm sm:text-base"
              data-testid="parts-filter-input"
            />
          </div>
          {/* Clear filters button */}
          {(truckFilter || trailerFilter || vinFilter || licenseFilter || partsFilter || searchQuery || statusFilter !== "all") && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setTruckFilter("");
                setTrailerFilter("");
                setVinFilter("");
                setLicenseFilter("");
                setPartsFilter("");
                setStatusFilter("all");
              }}
              className="text-slate-500 hover:text-slate-700 text-sm"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="text-center py-8 sm:py-12 text-slate-500 text-sm sm:text-base">Loading invoices...</div>
      ) : filteredInvoices.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="text-center py-8 sm:py-12">
            <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-700 mb-2">
              {searchQuery || statusFilter !== "all" ? "No invoices found" : "No invoices yet"}
            </h3>
            <p className="text-sm sm:text-base text-slate-500 mb-4">
              {searchQuery || statusFilter !== "all"
                ? "Try different search or filter criteria" 
                : "Create your first invoice to get started"}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <Link to="/invoices/new">
                <Button className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invoice
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {filteredInvoices.map((invoice, index) => (
            <Card 
              key={invoice.id} 
              className="card-hover border-slate-100 stagger-item"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`invoice-card-${invoice.id}`}
            >
              <CardContent className="p-3 sm:p-6">
                <div className="flex flex-col gap-3 sm:gap-4">
                  {/* Top Row: Invoice Info */}
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-slate-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
                      <FileText className="h-4 w-4 sm:h-6 sm:w-6 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base sm:text-lg text-slate-900 font-['Manrope']">
                          {invoice.invoice_number}
                        </h3>
                        <Badge 
                          variant={invoice.status === "paid" ? "default" : "secondary"}
                          className={`text-xs ${
                            invoice.status === "paid" 
                              ? "bg-emerald-100 text-emerald-700" 
                              : invoice.status === "awaiting_payment"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {invoice.status === "paid" ? "Paid" : invoice.status === "awaiting_payment" ? "Awaiting Payment" : "Estimate"}
                        </Badge>
                      </div>
                      <p className="text-sm sm:text-base text-slate-600 truncate">
                        {invoice.customer_name || "No customer assigned"}
                      </p>
                      {/* Vehicle Info: Truck #, VIN, and License */}
                      {(invoice.vehicle_info?.truck_number || invoice.vehicle_info?.serial_vin || invoice.vehicle_info?.license_number) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                          {invoice.vehicle_info?.truck_number && (
                            <p className="text-xs sm:text-sm text-slate-500">
                              <span className="font-medium">Truck #:</span> {invoice.vehicle_info.truck_number}
                            </p>
                          )}
                          {invoice.vehicle_info?.serial_vin && (
                            <p className="text-xs sm:text-sm text-slate-500">
                              <span className="font-medium">VIN:</span> {invoice.vehicle_info.serial_vin}
                            </p>
                          )}
                          {invoice.vehicle_info?.license_number && (
                            <p className="text-xs sm:text-sm text-slate-500">
                              <span className="font-medium">License:</span> {invoice.vehicle_info.license_number}
                            </p>
                          )}
                        </div>
                      )}
                      {/* Show matched parts when filtering by parts */}
                      {partsFilter && invoice.items?.some(item => 
                        item.parts_description?.toLowerCase().includes(partsFilter.toLowerCase())
                      ) && (
                        <div className="mt-1 p-2 bg-blue-50 rounded text-xs sm:text-sm">
                          <span className="font-medium text-blue-700">Parts found:</span>
                          <span className="text-blue-600 ml-1">
                            {invoice.items
                              .filter(item => item.parts_description?.toLowerCase().includes(partsFilter.toLowerCase()))
                              .map(item => item.parts_description)
                              .slice(0, 2)
                              .join(", ")}
                            {invoice.items.filter(item => 
                              item.parts_description?.toLowerCase().includes(partsFilter.toLowerCase())
                            ).length > 2 && "..."}
                          </span>
                        </div>
                      )}
                      <p className="text-xs sm:text-sm text-slate-400 mt-0.5 sm:mt-1">
                        Created: {formatDate(invoice.created_at)}
                      </p>
                    </div>
                    {/* Total - Mobile visible */}
                    <div className="text-right flex-shrink-0 sm:hidden">
                      <p className="text-xs text-slate-500">Total</p>
                      <p className="text-lg font-bold text-slate-900 font-mono">
                        ${calculateTotal(invoice).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Bottom Row: Actions */}
                  <div className="flex items-center justify-between pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                    {/* Total - Desktop visible */}
                    <div className="text-left hidden sm:block">
                      <p className="text-sm text-slate-500">Total Amount</p>
                      <p className="text-2xl font-bold text-slate-900 font-mono">
                        ${calculateTotal(invoice).toFixed(2)}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 w-full sm:w-auto justify-end">
                      <Link to={`/invoices/${invoice.id}`} className="flex-1 sm:flex-none">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="gap-1.5 sm:gap-2 w-full sm:w-auto text-xs sm:text-sm"
                          data-testid={`view-invoice-${invoice.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          <span>View</span>
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="gap-1.5 sm:gap-2 text-xs sm:text-sm"
                        onClick={() => handleDownloadPDF(invoice)}
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">PDF</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2 sm:px-3"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-invoice-${invoice.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && totalCount > 0 && (
        <div className="flex items-center justify-between mt-6 px-2">
          <div className="text-sm text-slate-500">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} invoices
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchInvoices(currentPage - 1)}
              disabled={!hasPrevPage || fetchingMore}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-slate-600 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchInvoices(currentPage + 1)}
              disabled={!hasNextPage || fetchingMore}
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice &quot;{selectedInvoice?.invoice_number}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-invoice-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
